import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { CURRENT_NETWORK } from '../config/networks';
import { detectProvider, isCorrectNetwork, switchToTargetNetwork } from '../utils/networkSwitch';

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children, onNotify }) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  const rawProviderRef = useRef(null);
  const initedRef = useRef(false);

  const setupProvider = (raw, addr) => {
    const web3Prov = new ethers.providers.Web3Provider(raw, 'any');
    setProvider(web3Prov);
    if (addr) {
      const s = web3Prov.getSigner(addr);
      setSigner(s);
    }
  };

  const connect = async () => {
    if (connecting) return;
    const raw = detectProvider();
    if (!raw) {
      if (onNotify) onNotify('error', 'No wallet detected. Install MetaMask or Trust Wallet.');
      return;
    }
    setConnecting(true);
    try {
      const accts = await raw.request({ method: 'eth_requestAccounts' });
      if (!accts || accts.length === 0) {
        if (onNotify) onNotify('error', 'No account selected');
        setConnecting(false);
        return;
      }
      const addr = ethers.utils.getAddress(accts[0]);
      const cid = await raw.request({ method: 'eth_chainId' });
      const cidNum = parseInt(cid, 16);

      rawProviderRef.current = raw;
      setAccount(addr);
      setChainId(cidNum);
      setupProvider(raw, addr);

      if (!isCorrectNetwork(cidNum)) {
        setSwitching(true);
        try {
          await switchToTargetNetwork(raw);
          if (onNotify) onNotify('success', `Switched to ${CURRENT_NETWORK.name}`);
        } catch (err) {
          if (onNotify) onNotify('error', 'Please switch network to continue');
        } finally {
          setSwitching(false);
        }
      } else {
        if (onNotify) onNotify('success', 'Wallet connected');
      }
    } catch (err) {
      if (onNotify) onNotify('error', err?.message || 'Failed to connect wallet');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    setAccount(null);
    setSigner(null);
    if (onNotify) onNotify('info', 'Wallet disconnected');
  };

  const requestSwitchNetwork = async () => {
    const raw = rawProviderRef.current || detectProvider();
    if (!raw) return;
    setSwitching(true);
    try {
      await switchToTargetNetwork(raw);
    } catch (err) {
      if (onNotify) onNotify('error', 'Network switch rejected');
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;

    const raw = detectProvider();
    if (!raw) return;
    rawProviderRef.current = raw;

    const tryEager = async () => {
      try {
        const accts = await raw.request({ method: 'eth_accounts' });
        if (accts && accts.length > 0) {
          const addr = ethers.utils.getAddress(accts[0]);
          const cid = await raw.request({ method: 'eth_chainId' });
          const cidNum = parseInt(cid, 16);
          setAccount(addr);
          setChainId(cidNum);
          setupProvider(raw, addr);
          if (!isCorrectNetwork(cidNum)) {
            setSwitching(true);
            try {
              await switchToTargetNetwork(raw);
            } catch (_) {}
            setSwitching(false);
          }
        }
      } catch (_) {}
    };
    tryEager();

    const handleAccountsChanged = (accts) => {
      if (!accts || accts.length === 0) {
        setAccount(null);
        setSigner(null);
        if (onNotify) onNotify('info', 'Wallet disconnected');
      } else {
        const addr = ethers.utils.getAddress(accts[0]);
        setAccount(addr);
        setupProvider(raw, addr);
        if (onNotify) onNotify('info', `Account changed`);
      }
    };

    const handleChainChanged = (cid) => {
      const cidNum = typeof cid === 'string' ? parseInt(cid, 16) : Number(cid);
      setChainId(cidNum);
      setupProvider(raw, account);
      if (!isCorrectNetwork(cidNum)) {
        if (onNotify) onNotify('warning', 'Wrong network detected. Switching...');
        switchToTargetNetwork(raw).catch(() => {
          if (onNotify) onNotify('error', 'Please switch network manually');
        });
      } else {
        if (onNotify) onNotify('success', 'Network ready');
      }
    };

    if (raw.on) {
      raw.on('accountsChanged', handleAccountsChanged);
      raw.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (raw.removeListener) {
        raw.removeListener('accountsChanged', handleAccountsChanged);
        raw.removeListener('chainChanged', handleChainChanged);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrongNetwork = chainId !== null && !isCorrectNetwork(chainId);

  const value = {
    account,
    chainId,
    provider,
    signer,
    connecting,
    switching,
    wrongNetwork,
    connect,
    disconnect,
    requestSwitchNetwork
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
